import { DatePipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { switchMap } from 'rxjs/operators'

import {
    BaseComponent,
    CREW_SCHEDULE_CONTEXT_DAYS,
    CREW_SCHEDULE_WEEKDAYS,
    CrewMemberReassignment,
    CrewScheduleContext,
    CrewScheduleForeman,
    CrewScheduleMember,
    Division,
    FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY,
    ForemanScheduleGroupBy,
    FullCrewScheduleProjection,
    FullScheduleSegmentInput,
    IsoDate,
    SavedForemanSegment,
    SavedWeekendEntry,
    TimeOffEntry,
    VpJob,
    addDaysIso,
    buildJobOrderIndex,
    collectWeekdaysFrom,
    compareForemanScheduleJobGroups,
    isReassignmentActiveOnDate,
    isoDateStringForApi,
    resolveEffectiveMembers,
    todayIso,
} from '../../../shared'
import { AutoSizeDirective } from '../../../shared/utility/auto-size.directive'

/** A reassignment chip shown in a foreman cell (lent out or borrowed in). */
export interface SummaryReassignmentChip {
    initials: string
    label: string
    tooltip: string
}

/** A crew member who is off on a given day, surfaced as a chip in the day column. */
export interface SummaryOffMemberVm {
    hrRef: number
    name: string
    initials: string
    status: 'Approved' | 'Pending'
    leaveType: string
}

export interface SummaryGridColumn {
    key: string
    date: IsoDate
    label: string
    isToday: boolean
}

/** Per-day projected assignment used when building summary cells. */
interface SummaryDayAssignment {
    vpJobId: string
    phase: string
    isOverHours: boolean
    remainingHoursAtEnd: number
    overThresholdHours: number
}

export interface SummaryCellVm {
    key: string
    date: IsoDate
    isToday: boolean
    isHoliday: boolean
    holidayName: string | null
    vpJobId: string | null
    phase: string | null
    /** True when this day was scheduled past the phase's remaining hours via a forced day count. */
    isOverHours?: boolean
    /** True when scheduled work falls beyond the phase's estimated hours. */
    isOverEstimate?: boolean
    /** Effective foreman name for a person-row cell (null for foreman rows). */
    foremanName?: string | null
    /** Set on a person-row cell when the person is working under a different foreman that day. */
    lentNote?: string | null
    lentOut: SummaryReassignmentChip[]
    borrowedIn: SummaryReassignmentChip[]
    /** Crew members off this day, shown as initials chips. */
    offMembers?: SummaryOffMemberVm[]
}

export interface SummaryForemanRowVm {
    hrRef: number
    name: string
    jobTitle: string
    divisionId: string | null
    divisionName: string | null
    cells: SummaryCellVm[]
}

/** A division or job grouping of foreman rows for the summary template. */
export interface SummaryForemanGroupVm {
    key: string
    label: string
    groupJobId: string | null
    divisionId: string | null
    rows: SummaryForemanRowVm[]
}

export interface SummaryPersonRowVm {
    hrRef: number
    name: string
    jobTitle: string
    isForeman: boolean
    isSelected: boolean
    cells: SummaryCellVm[]
}

export interface SummaryMemberOption {
    hrRef: number
    name: string
    jobTitle: string
}

function newClientId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `seg-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

@Component({
    selector: 'app-foreman-schedule-summary-tab',
    templateUrl: './foreman-schedule-summary-tab.component.html',
    styleUrls: ['./foreman-schedule-summary-tab.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, FormsModule, AutoSizeDirective],
})
export class ForemanScheduleSummaryTabComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)
    private static readonly GROUP_BY_STORAGE_KEY = 'foremanSchedule.summaryGroupBy'

    readonly isActive = input(false)
    readonly activeJobs = input.required<VpJob[]>()
    readonly reassignments = input<CrewMemberReassignment[]>([])
    readonly savedSegments = input<SavedForemanSegment[]>([])
    readonly savedWeekendEntries = input<SavedWeekendEntry[]>([])
    /** Bumped by the shell when the foreman roster changes; triggers a context reload. */
    readonly foremanRevision = input(0)

    readonly isLoading = signal(false)
    readonly loadError = signal<string | null>(null)
    readonly context = signal<CrewScheduleContext | null>(null)
    readonly projection = signal<FullCrewScheduleProjection | null>(null)

    /** Job filter; empty string means "all jobs". */
    readonly selectedJobId = signal('')
    /** Person filter; null means "everyone". */
    readonly selectedMemberHrRef = signal<number | null>(null)

    /** Active divisions for the toolbar filter dropdown, sorted by name. */
    readonly divisionOptions = signal<Division[]>([])
    /** Selected division filter; `''` means All (show every foreman). */
    readonly selectedDivisionId = signal('')

    /** Toolbar grouping mode; defaults to division, persisted per tab in localStorage. */
    readonly groupBy = signal<ForemanScheduleGroupBy>(this.readStoredGroupBy())

    protected readonly crewScheduleWeekdayCount = CREW_SCHEDULE_WEEKDAYS

    private hasLoaded = false
    private autoSelectApplied = false
    private reassignmentsSnapshot = ''
    private savedSegmentsSnapshot = ''
    private savedWeekendEntriesSnapshot = ''
    private foremanRevisionSnapshot = 0

    readonly gridWeekdays = computed(() => collectWeekdaysFrom(todayIso(), CREW_SCHEDULE_WEEKDAYS))

    readonly gridStart = computed(() => {
        const weekdays = this.gridWeekdays()
        return weekdays.length ? weekdays[0] : todayIso()
    })

    readonly gridRangeEnd = computed(() => {
        const weekdays = this.gridWeekdays()
        return weekdays.length ? weekdays[weekdays.length - 1] : todayIso()
    })

    readonly gridColumns = computed((): SummaryGridColumn[] => {
        const today = todayIso()
        return this.gridWeekdays().map(date => ({
            key: date,
            date,
            label: this.formatShortDate(date),
            isToday: date === today,
        }))
    })

    readonly gridTemplateColumns = computed(() => {
        const parts = ['var(--summary-name-col-width, 14rem)']
        for (let i = 0; i < this.gridColumns().length; i++) parts.push('7rem')
        return parts.join(' ')
    })

    readonly holidayByDate = computed(() => {
        const map = new Map<IsoDate, string>()
        for (const h of this.context()?.holidays ?? []) {
            const date = isoDateStringForApi(h.date as string | Date | null | undefined)
            if (date) map.set(date, h.name)
        }
        return map
    })

    /** Deduped list of crew members across all foremen, for the person filter dropdown. */
    readonly memberOptions = computed((): SummaryMemberOption[] => {
        const map = new Map<number, SummaryMemberOption>()
        for (const f of this.context()?.foremen ?? []) {
            for (const m of f.members ?? []) {
                if (!map.has(m.hrRef)) map.set(m.hrRef, { hrRef: m.hrRef, name: m.name, jobTitle: m.jobTitle })
            }
        }
        return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
    })

    /** Per-foreman map of date -> projected assignment, for days with scheduled work. */
    private readonly assignmentsByForeman = computed(() => {
        const map = new Map<number, Map<IsoDate, SummaryDayAssignment>>()
        const proj = this.projection()
        if (!proj) return map
        for (const f of proj.foremen ?? []) {
            const dayMap = new Map<IsoDate, SummaryDayAssignment>()
            for (const s of f.segments ?? []) {
                const overThresholdHours = s.overThresholdHours ?? 0
                for (const d of s.days ?? []) {
                    if (d.hoursScheduled > 0) {
                        const date = isoDateStringForApi(d.date as string | Date | null | undefined)
                        if (date) {
                            dayMap.set(date, {
                                vpJobId: d.vpJobId,
                                phase: d.phase,
                                isOverHours: !!d.isOverHours,
                                remainingHoursAtEnd: d.remainingHoursAtEnd ?? 0,
                                overThresholdHours,
                            })
                        }
                    }
                }
            }
            map.set(f.foremanHrRef, dayMap)
        }
        return map
    })

    readonly foremanRows = computed((): SummaryForemanRowVm[] => {
        const ctx = this.context()
        if (!ctx) return []
        const assignments = this.assignmentsByForeman()
        const cols = this.gridColumns()
        const holidays = this.holidayByDate()
        const reassignments = this.reassignments()
        const jobFilter = this.normalizeJobId(this.selectedJobId())

        const rows = ctx.foremen.map(f => {
            const dayMap = assignments.get(f.hrRef) ?? new Map<IsoDate, SummaryDayAssignment>()
            const cells = cols.map(col => {
                const a = dayMap.get(col.date) ?? null
                const matchesFilter = !jobFilter || (!!a && this.normalizeJobId(a.vpJobId) === jobFilter)
                const show = a && matchesFilter
                const overFlags = show ? this.summaryOverFlags(a!) : { isOverHours: false, isOverEstimate: false }
                return {
                    key: col.key,
                    date: col.date,
                    isToday: col.isToday,
                    isHoliday: !!holidays.get(col.date),
                    holidayName: holidays.get(col.date) ?? null,
                    vpJobId: show ? a!.vpJobId : null,
                    phase: show ? a!.phase : null,
                    isOverHours: overFlags.isOverHours,
                    isOverEstimate: overFlags.isOverEstimate,
                    lentOut: this.lentOutChips(f.hrRef, col.date, reassignments),
                    borrowedIn: this.borrowedInChips(f.hrRef, col.date, reassignments),
                    offMembers: this.buildOffMembers(f, col.date, ctx.foremen, reassignments),
                } as SummaryCellVm
            })
            return {
                hrRef: f.hrRef,
                name: f.name,
                jobTitle: f.jobTitle,
                divisionId: f.divisionId ?? null,
                divisionName: f.divisionName ?? null,
                cells,
            }
        })

        if (jobFilter) return rows.filter(r => r.cells.some(c => !!c.vpJobId))
        return rows
    })

    /** Rows after applying the toolbar division filter (`''` = All). */
    readonly filteredForemanRows = computed((): SummaryForemanRowVm[] => {
        const selected = this.selectedDivisionId()
        const rows = this.foremanRows()
        if (!selected) return rows
        return rows.filter(row => row.divisionId === selected)
    })

    /** Filtered rows grouped by division or job. */
    readonly foremanGroups = computed((): SummaryForemanGroupVm[] => {
        const rows = this.filteredForemanRows()
        if (this.groupBy() === 'job') {
            return this.buildJobForemanGroups(rows)
        }
        return this.buildDivisionForemanGroups(rows)
    })

    readonly hasVisibleForemen = computed(() => this.filteredForemanRows().length > 0)

    /** The selected person's home crew (foreman + all members), one row each. */
    readonly crewRows = computed((): SummaryPersonRowVm[] | null => {
        const hrRef = this.selectedMemberHrRef()
        if (hrRef == null) return null
        const ctx = this.context()
        if (!ctx) return null
        const home = this.homeForemanForMember(hrRef)
        if (!home) return null

        const selectedDivision = this.selectedDivisionId()
        if (selectedDivision && (home.divisionId ?? null) !== selectedDivision) return []

        return (home.members ?? []).map(m => ({
            hrRef: m.hrRef,
            name: m.name,
            jobTitle: m.jobTitle,
            isForeman: m.isForeman,
            isSelected: m.hrRef === hrRef,
            cells: this.buildPersonCells(m.hrRef),
        }))
    })

    /** Per-day cells for one crew member: their effective job/phase plus an off badge on days off. */
    private buildPersonCells(memberHrRef: number): SummaryCellVm[] {
        const member = this.findMember(memberHrRef)
        const assignments = this.assignmentsByForeman()
        const cols = this.gridColumns()
        const holidays = this.holidayByDate()
        const reassignments = this.reassignments()
        const jobFilter = this.normalizeJobId(this.selectedJobId())
        const homeForeman = this.homeForemanForMember(memberHrRef)

        return cols.map(col => {
            const effForeman = this.effectiveForemanForMember(memberHrRef, col.date, reassignments)
            const dayMap = effForeman ? assignments.get(effForeman.hrRef) : undefined
            const a = dayMap?.get(col.date) ?? null
            const matchesFilter = !jobFilter || (!!a && this.normalizeJobId(a.vpJobId) === jobFilter)
            const show = a && matchesFilter
            const overFlags = show ? this.summaryOverFlags(a!) : { isOverHours: false, isOverEstimate: false }
            const lentAway = !!effForeman && !!homeForeman && effForeman.hrRef !== homeForeman.hrRef
            const offEntry = member ? this.memberTimeOffOn(member, col.date) : null
            return {
                key: col.key,
                date: col.date,
                isToday: col.isToday,
                isHoliday: !!holidays.get(col.date),
                holidayName: holidays.get(col.date) ?? null,
                vpJobId: show ? a!.vpJobId : null,
                phase: show ? a!.phase : null,
                isOverHours: overFlags.isOverHours,
                isOverEstimate: overFlags.isOverEstimate,
                foremanName: show ? effForeman?.name ?? null : null,
                lentNote: lentAway && effForeman ? `with ${this.shortName(effForeman.name)}` : null,
                lentOut: [],
                borrowedIn: [],
                offMembers:
                    offEntry && member
                        ? [
                              {
                                  hrRef: member.hrRef,
                                  name: member.name,
                                  initials: this.initials(member.name),
                                  status: offEntry.status,
                                  leaveType: offEntry.leaveType?.trim() || 'Time off',
                              },
                          ]
                        : [],
            } as SummaryCellVm
        })
    }

    constructor() {
        super()

        effect(() => {
            if (this.isActive() && !this.hasLoaded) {
                this.loadContext()
            }
        })

        // Once the crew context is loaded, default "Who am I?" to the logged-in user when they
        // are a foreman or crew member. Runs once so manual changes and refreshes are preserved.
        effect(() => {
            if (this.autoSelectApplied) return
            const opts = this.memberOptions()
            if (!opts.length) return
            this.autoSelectApplied = true
            const myHrRef = this.services.authentication.hrref
            if (myHrRef > 0 && opts.some(m => m.hrRef === myHrRef)) {
                this.selectedMemberHrRef.set(myHrRef)
            }
        })

        effect(() => {
            const snapshot = JSON.stringify(this.reassignments())
            if (snapshot === this.reassignmentsSnapshot) return
            this.reassignmentsSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const snapshot = JSON.stringify(this.savedSegments())
            if (snapshot === this.savedSegmentsSnapshot) return
            this.savedSegmentsSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const snapshot = JSON.stringify(this.savedWeekendEntries())
            if (snapshot === this.savedWeekendEntriesSnapshot) return
            this.savedWeekendEntriesSnapshot = snapshot
            if (!this.hasLoaded) return
            this.runProjection()
        })

        effect(() => {
            const revision = this.foremanRevision()
            if (revision === this.foremanRevisionSnapshot) return
            this.foremanRevisionSnapshot = revision
            if (!this.hasLoaded) return
            if (this.isActive()) {
                this.refresh()
            } else {
                this.hasLoaded = false
            }
        })
    }

    refresh() {
        this.hasLoaded = false
        this.projection.set(null)
        this.loadContext()
    }

    onJobFilterChange(jobId: string) {
        this.selectedJobId.set(this.normalizeJobId(jobId))
    }

    onMemberFilterChange(value: number | null) {
        this.selectedMemberHrRef.set(value == null ? null : value)
    }

    /** Sets the toolbar division filter (`''` = All). */
    onDivisionFilterChange(value: string) {
        this.selectedDivisionId.set(value ?? '')
    }

    onGroupByChange(value: ForemanScheduleGroupBy) {
        const mode: ForemanScheduleGroupBy = value === 'job' ? 'job' : 'division'
        this.groupBy.set(mode)
        try {
            localStorage.setItem(ForemanScheduleSummaryTabComponent.GROUP_BY_STORAGE_KEY, mode)
        } catch {
            /* ignore storage failures */
        }
    }

    normalizeJobId(jobId: string | null | undefined): string {
        return (jobId ?? '').trim()
    }

    jobLabel(jobId: string): string {
        const id = this.normalizeJobId(jobId)
        if (!id) return ''
        const job = this.activeJobs().find(j => this.normalizeJobId(j.id) === id)
        return job ? `${id} — ${job.name}` : id
    }

    cellTooltip(cell: SummaryCellVm): string {
        if (cell.vpJobId) {
            const base = this.jobLabel(cell.vpJobId)
            const phase = cell.phase ? ` / ${cell.phase}` : ''
            const foreman = cell.foremanName ? `\nForeman: ${cell.foremanName}` : ''
            const overNote = cell.isOverEstimate ? '\nOver estimated hours' : ''
            const overHoursNote = cell.isOverHours ? '\nOver remaining hours (forced day)' : ''
            return `${base}${phase}${foreman}${overNote}${overHoursNote}`
        }
        if (cell.isHoliday) return cell.holidayName ? `Company holiday: ${cell.holidayName}` : 'Company holiday'
        return ''
    }

    private readStoredGroupBy(): ForemanScheduleGroupBy {
        try {
            const stored = localStorage.getItem(ForemanScheduleSummaryTabComponent.GROUP_BY_STORAGE_KEY)
            if (stored === 'job' || stored === 'division') return stored
        } catch {
            /* ignore storage failures */
        }
        return 'division'
    }

    private buildDivisionForemanGroups(rows: SummaryForemanRowVm[]): SummaryForemanGroupVm[] {
        const groups = new Map<string, SummaryForemanGroupVm>()
        for (const row of rows) {
            const key = row.divisionId ?? ''
            let group = groups.get(key)
            if (!group) {
                const divisionName = row.divisionName || 'No Division'
                group = {
                    key: key || 'no-division',
                    label: divisionName,
                    groupJobId: null,
                    divisionId: row.divisionId,
                    rows: [],
                }
                groups.set(key, group)
            }
            group.rows.push(row)
        }

        return [...groups.values()].sort((a, b) => {
            if (!a.divisionId && b.divisionId) return 1
            if (a.divisionId && !b.divisionId) return -1
            return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        })
    }

    private buildJobForemanGroups(rows: SummaryForemanRowVm[]): SummaryForemanGroupVm[] {
        const jobOrderIndex = buildJobOrderIndex(this.activeJobs())
        const groups = new Map<string, SummaryForemanGroupVm>()
        this.seedActiveJobGroups(groups)

        for (const row of rows) {
            const jobIds = new Set<string>()
            for (const cell of row.cells) {
                const jobId = this.normalizeJobId(cell.vpJobId)
                if (jobId) jobIds.add(jobId)
            }

            if (jobIds.size === 0) {
                let group = groups.get(FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY)
                if (!group) {
                    group = {
                        key: FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY,
                        label: 'No job scheduled',
                        groupJobId: null,
                        divisionId: null,
                        rows: [],
                    }
                    groups.set(FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY, group)
                }
                group.rows.push(row)
                continue
            }

            for (const jobId of jobIds) {
                const key = `job-${jobId}`
                let group = groups.get(key)
                if (!group) {
                    group = {
                        key,
                        label: this.jobLabel(jobId),
                        groupJobId: jobId,
                        divisionId: null,
                        rows: [],
                    }
                    groups.set(key, group)
                }
                group.rows.push(this.maskSummaryRowForJob(row, jobId))
            }
        }

        return [...groups.values()].sort((a, b) =>
            compareForemanScheduleJobGroups(
                { groupJobId: a.groupJobId },
                { groupJobId: b.groupJobId },
                jobOrderIndex,
            ),
        )
    }

    /** Pre-create a group for every active job so unscheduled jobs are visible when grouping by job. */
    private seedActiveJobGroups(groups: Map<string, SummaryForemanGroupVm>): void {
        const jobFilter = this.normalizeJobId(this.selectedJobId())
        for (const job of this.activeJobs()) {
            const jobId = this.normalizeJobId(job.id)
            if (!jobId) continue
            if (jobFilter && jobId !== jobFilter) continue
            const key = `job-${jobId}`
            groups.set(key, {
                key,
                label: `${jobId} — ${job.name}`,
                groupJobId: jobId,
                divisionId: null,
                rows: [],
            })
        }
    }

    private maskSummaryRowForJob(row: SummaryForemanRowVm, jobId: string): SummaryForemanRowVm {
        const normalizedJobId = this.normalizeJobId(jobId)
        return {
            ...row,
            cells: row.cells.map(cell => {
                const cellJobId = this.normalizeJobId(cell.vpJobId)
                if (!cellJobId || cellJobId === normalizedJobId) return cell
                return {
                    ...cell,
                    vpJobId: null,
                    phase: null,
                    isOverHours: false,
                    isOverEstimate: false,
                }
            }),
        }
    }

    private loadContext() {
        this.isLoading.set(true)
        this.loadError.set(null)

        const start = todayIso()
        const end = addDaysIso(start, CREW_SCHEDULE_CONTEXT_DAYS)
        const params = new HttpParams().set('startDate', start).set('endDate', end)

        this.cancelableRequest(
            'summary-context',
            true,
            this.services.data
                .load(
                    true,
                    false,
                    this.services.data
                        .get<{ ready: boolean }>(
                            'api/ForemanSchedule/WarmCrewScheduleProductivityCache',
                            new HttpParams(),
                            Object,
                        )
                        .pipe(
                            switchMap(() =>
                                this.services.data.get<CrewScheduleContext>(
                                    'api/ForemanSchedule/GetCrewScheduleContext',
                                    params,
                                    Object,
                                ),
                            ),
                        ),
                    this.services.data.divisions.queryAll(),
                )
                .subscribe({
                    next: ([data, divisions]) => {
                        this.hasLoaded = true
                        this.divisionOptions.set(
                            ((divisions ?? []) as Division[])
                                .filter(d => !d.archived)
                                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
                        )
                        this.context.set(this.normalizeContext(data))
                        this.runProjection()
                    },
                    error: () => {
                        this.isLoading.set(false)
                        this.loadError.set('Failed to load schedule summary.')
                    },
                }),
        )
    }

    private runProjection() {
        if (!this.context()) return
        this.isLoading.set(true)

        const request = {
            startDate: this.gridStart(),
            maxProjectionDays: CREW_SCHEDULE_CONTEXT_DAYS,
            hoursPerPerson: 10,
            segments: this.buildSegmentInputs(),
            weekendEntries: this.weekendEntriesToApi(),
            reassignments: this.reassignmentsToApi(),
        }

        this.services.data
            .post<FullCrewScheduleProjection>('api/ForemanSchedule/ProjectFullCrewSchedule', request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: projection => {
                    this.projection.set(projection)
                    this.isLoading.set(false)
                },
                error: () => {
                    this.isLoading.set(false)
                    this.loadError.set('Failed to project the schedule summary.')
                },
            })
    }

    private buildSegmentInputs(): FullScheduleSegmentInput[] {
        const saved = (this.savedSegments() ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
        return saved.map(s => ({
            clientId: newClientId(),
            foremanHrRef: s.foremanHrRef,
            vpJobId: this.normalizeJobId(s.vpJobId),
            phase: (s.phase ?? '').trim(),
            startDate: isoDateStringForApi(s.startDate as string | Date | null | undefined),
            sortOrder: s.sortOrder,
            endDateExclusive: s.endDateExclusive
                ? isoDateStringForApi(s.endDateExclusive as string | Date | null | undefined) || null
                : null,
            forcedDays: s.forcedDays ?? null,
        }))
    }

    private summaryOverFlags(a: SummaryDayAssignment): { isOverHours: boolean; isOverEstimate: boolean } {
        return {
            isOverHours: !!a.isOverHours,
            isOverEstimate: a.overThresholdHours > 0 && a.remainingHoursAtEnd < a.overThresholdHours - 0.001,
        }
    }

    private weekendEntriesToApi(): SavedWeekendEntry[] {
        return (this.savedWeekendEntries() ?? []).map(e => ({
            id: e.id,
            foremanHrRef: e.foremanHrRef,
            date: isoDateStringForApi(e.date as string | Date | null | undefined),
            vpJobId: e.vpJobId,
            phase: e.phase,
            hours: e.hours ?? 0,
        }))
    }

    private reassignmentsToApi() {
        return (this.reassignments() ?? []).map(r => ({
            memberHrRef: r.memberHrRef,
            fromForemanHrRef: r.fromForemanHrRef,
            toForemanHrRef: r.toForemanHrRef,
            startDate: r.startDate,
            endDate: r.endDate,
        }))
    }

    /** Off crew members (effective roster) for a foreman on a given weekday. */
    private buildOffMembers(
        foreman: CrewScheduleForeman,
        date: IsoDate,
        allForemen: CrewScheduleForeman[],
        reassignments: CrewMemberReassignment[],
    ): SummaryOffMemberVm[] {
        const off: SummaryOffMemberVm[] = []
        for (const member of resolveEffectiveMembers(foreman, date, allForemen, reassignments)) {
            const entry = this.memberTimeOffOn(member, date)
            if (!entry) continue
            off.push({
                hrRef: member.hrRef,
                name: member.name,
                initials: this.initials(member.name),
                status: entry.status,
                leaveType: entry.leaveType?.trim() || 'Time off',
            })
        }
        return off
    }

    offStripTooltip(cell: SummaryCellVm): string {
        const off = cell.offMembers ?? []
        if (!off.length) return ''
        const lines = off.map(m => `${m.name} — ${m.leaveType} (${m.status})`)
        return `Off this day:\n${lines.join('\n')}`
    }

    private memberTimeOffOn(member: CrewScheduleMember, date: IsoDate): TimeOffEntry | null {
        for (const entry of member.timeOff ?? []) {
            const start = isoDateStringForApi(entry.startDate as string | Date | null | undefined)
            const end = isoDateStringForApi(entry.endDate as string | Date | null | undefined)
            if (start && end && start <= date && end >= date) return entry
        }
        return null
    }

    private lentOutChips(
        foremanHrRef: number,
        date: IsoDate,
        reassignments: CrewMemberReassignment[],
    ): SummaryReassignmentChip[] {
        return (reassignments ?? [])
            .filter(r => r.fromForemanHrRef === foremanHrRef && isReassignmentActiveOnDate(r, date))
            .map(r => ({
                initials: this.initials(r.memberName),
                label: `${this.initials(r.memberName)} → ${this.shortName(r.toForemanName)}`,
                tooltip: `${r.memberName} working with ${r.toForemanName}`,
            }))
    }

    private borrowedInChips(
        foremanHrRef: number,
        date: IsoDate,
        reassignments: CrewMemberReassignment[],
    ): SummaryReassignmentChip[] {
        return (reassignments ?? [])
            .filter(r => r.toForemanHrRef === foremanHrRef && isReassignmentActiveOnDate(r, date))
            .map(r => ({
                initials: this.initials(r.memberName),
                label: `+${this.initials(r.memberName)}`,
                tooltip: `${r.memberName} borrowed from ${r.fromForemanName}`,
            }))
    }

    private effectiveForemanForMember(
        memberHrRef: number,
        date: IsoDate,
        reassignments: CrewMemberReassignment[],
    ): CrewScheduleForeman | null {
        const active = (reassignments ?? []).find(
            r => r.memberHrRef === memberHrRef && isReassignmentActiveOnDate(r, date),
        )
        const ctx = this.context()
        if (!ctx) return null
        if (active) return ctx.foremen.find(f => f.hrRef === active.toForemanHrRef) ?? null
        return this.homeForemanForMember(memberHrRef)
    }

    private homeForemanForMember(memberHrRef: number): CrewScheduleForeman | null {
        const ctx = this.context()
        if (!ctx) return null
        return ctx.foremen.find(f => (f.members ?? []).some(m => m.hrRef === memberHrRef)) ?? null
    }

    private findMember(memberHrRef: number): CrewScheduleMember | null {
        for (const f of this.context()?.foremen ?? []) {
            const found = (f.members ?? []).find(m => m.hrRef === memberHrRef)
            if (found) return found
        }
        return null
    }

    private normalizeContext(data: CrewScheduleContext): CrewScheduleContext {
        return {
            ...data,
            dateRangeStart: isoDateStringForApi(data.dateRangeStart as string | Date | null | undefined),
            dateRangeEnd: isoDateStringForApi(data.dateRangeEnd as string | Date | null | undefined),
            foremen: (data.foremen ?? []).map(f => ({
                ...f,
                members: (f.members ?? []).map(m => ({
                    ...m,
                    timeOff: (m.timeOff ?? []).map(t => ({
                        ...t,
                        startDate: isoDateStringForApi(t.startDate as string | Date | null | undefined),
                        endDate: isoDateStringForApi(t.endDate as string | Date | null | undefined),
                    })),
                })),
            })),
            holidays: (data.holidays ?? []).map(h => ({
                ...h,
                date: isoDateStringForApi(h.date as string | Date | null | undefined),
            })),
        }
    }

    private initials(name: string): string {
        const words = (name ?? '').replace(/,/g, ' ').split(/\s+/).filter(Boolean)
        if (!words.length) return '?'
        const letters = words.slice(0, 2).map(w => w[0]?.toUpperCase() ?? '')
        return letters.join('') || '?'
    }

    /** Short, human label for a foreman: surname when "Last, First", else first word. */
    private shortName(name: string): string {
        const trimmed = (name ?? '').trim()
        if (!trimmed) return ''
        if (trimmed.includes(',')) return trimmed.split(',')[0].trim()
        const words = trimmed.split(/\s+/).filter(Boolean)
        return words[words.length - 1] ?? trimmed
    }

    private formatShortDate(iso: IsoDate): string {
        const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
